import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { ScrollArea } from "./components/ui/scroll-area";
import {
  Plus, Trash2, Save, Copy, Upload, Wand2, Clock, 
  ArrowLeft, GripVertical, Play, Image, Phone, FileText,
  Radio, Users, User, MessageSquare, Newspaper, Twitter,
  Activity, ChevronDown, ChevronUp, Edit2, Check, X,
  Download, RefreshCw, Eye, EyeOff
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Event type configuration
const EVENT_TYPES = {
  media: { label: "📰 Médias", color: "bg-blue-500", icon: Newspaper },
  social: { label: "📱 Réseaux sociaux", color: "bg-pink-500", icon: Twitter },
  player_message: { label: "💬 Message joueur", color: "bg-green-500", icon: MessageSquare },
  animator_action: { label: "🎭 Action animateur", color: "bg-orange-500", icon: Users },
  radio_flash: { label: "📻 Flash radio", color: "bg-purple-500", icon: Radio }
};

const CONTENT_TYPES = {
  text: { label: "Texte", icon: FileText },
  image: { label: "Image IA", icon: Image },
  phone_call: { label: "Appel téléphonique", icon: Phone }
};

const CRISIS_TYPES = [
  { value: "incendie", label: "🔥 Incendie industriel" },
  { value: "cyberattaque", label: "💻 Cyberattaque" },
  { value: "suicide", label: "😢 Suicide d'un salarié" },
  { value: "agression", label: "⚠️ Agression sexuelle / Viol" },
  { value: "deces", label: "💀 Décès accidentel" },
  { value: "vol", label: "📦 Vol à grande échelle" },
  { value: "fraude", label: "💰 Détournement financier" },
  { value: "concurrent", label: "🇨🇳 Concurrent agressif" },
  { value: "produit", label: "📦 Crise produit" },
  { value: "maritime", label: "⚓ Crise maritime" },
  { value: "sociale", label: "👥 Crise sociale" },
  { value: "custom", label: "✨ Personnalisé" }
];

// Scenario Templates List Component
export const ScenarioTemplatesList = ({ token }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [preloaded, setPreloaded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scenario-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates || []);
      setPreloaded(res.data.preloaded || []);
    } catch (err) {
      toast.error("Erreur de chargement des scénarios");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleDuplicate = async (templateId) => {
    try {
      const res = await axios.post(`${API}/scenario-templates/${templateId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Scénario dupliqué !");
      loadTemplates();
      navigate(`/admin/scenarios/${res.data.id}`);
    } catch (err) {
      toast.error("Erreur de duplication");
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Supprimer ce scénario ?")) return;
    try {
      await axios.delete(`${API}/scenario-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Scénario supprimé");
      loadTemplates();
    } catch (err) {
      toast.error("Erreur de suppression");
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold heading text-primary">Éditeur de Scénarios</h1>
          <p className="text-muted-foreground text-sm">Créez, modifiez et gérez vos scénarios de crise</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAIModal(true)} className="bg-purple-600 hover:bg-purple-700">
            <Wand2 className="w-4 h-4 mr-2" /> Générer avec IA
          </Button>
          <Button onClick={() => setShowNewModal(true)} className="bg-primary text-primary-foreground">
            <Plus className="w-4 h-4 mr-2" /> Nouveau scénario
          </Button>
        </div>
      </div>

      {/* Templates personnalisés */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Edit2 className="w-5 h-5 text-primary" /> Mes scénarios
        </h2>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Chargement...</div>
        ) : templates.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground crisis-panel">
            <p>Aucun scénario personnalisé</p>
            <p className="text-sm">Créez-en un ou dupliquez un modèle existant</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(template => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="crisis-panel p-4 hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/admin/scenarios/${template.id}`)}
              >
                <div className="flex items-start justify-between mb-2">
                  <Badge className="badge-matrix">{template.crisis_type}</Badge>
                  <span className="text-xs text-muted-foreground">{template.events?.length || 0} événements</span>
                </div>
                <h3 className="font-semibold text-primary mb-1">{template.name}</h3>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{template.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {template.duration_minutes} min
                  </span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDuplicate(template.id); }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(template.id); }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Scénarios préchargés */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-muted-foreground" /> Modèles disponibles
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {preloaded.map(template => (
            <div
              key={template.id}
              className="crisis-panel p-4 opacity-80 hover:opacity-100 transition-opacity"
            >
              <div className="flex items-start justify-between mb-2">
                <Badge variant="outline">{template.crisis_type}</Badge>
                <span className="text-xs text-muted-foreground">{template.events?.length || 0} événements</span>
              </div>
              <h3 className="font-semibold mb-1">{template.name}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{template.description}</p>
              <Button size="sm" variant="outline" className="w-full" onClick={() => handleDuplicate(template.id)}>
                <Copy className="w-4 h-4 mr-2" /> Dupliquer et modifier
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Nouveau Scénario */}
      <AnimatePresence>
        {showNewModal && (
          <NewScenarioModal token={token} onClose={() => setShowNewModal(false)} onCreated={(id) => {
            setShowNewModal(false);
            navigate(`/admin/scenarios/${id}`);
          }} />
        )}
      </AnimatePresence>

      {/* Modal Génération IA */}
      <AnimatePresence>
        {showAIModal && (
          <AIGenerateModal token={token} onClose={() => setShowAIModal(false)} onCreated={(id) => {
            setShowAIModal(false);
            navigate(`/admin/scenarios/${id}`);
          }} />
        )}
      </AnimatePresence>
    </div>
  );
};

// New Scenario Modal
const NewScenarioModal = ({ token, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    crisis_type: "incendie",
    duration_minutes: 180
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/scenario-templates`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Scénario créé !");
      onCreated(res.data.id);
    } catch (err) {
      toast.error("Erreur de création");
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="crisis-panel p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold heading text-primary mb-4">Nouveau Scénario</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Nom</label>
            <Input
              className="input-matrix"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Incendie site industriel"
              required
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Type de crise</label>
            <select
              className="input-matrix w-full h-10 px-3"
              value={formData.crisis_type}
              onChange={e => setFormData({ ...formData, crisis_type: e.target.value })}
            >
              {CRISIS_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Description</label>
            <Textarea
              className="input-matrix"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Décrivez le contexte du scénario..."
              rows={3}
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Durée (minutes)</label>
            <Input
              type="number"
              className="input-matrix"
              value={formData.duration_minutes}
              onChange={e => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
              min={30}
              max={480}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button type="submit" className="flex-1 bg-primary text-primary-foreground" disabled={loading}>
              {loading ? "Création..." : "Créer"}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// AI Generate Modal
const AIGenerateModal = ({ token, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    crisis_type: "incendie",
    description: "",
    company_type: "Entreprise industrielle",
    duration_minutes: 180,
    num_events: 15
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/scenario-templates/generate-ai`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Scénario généré par IA !");
      onCreated(res.data.id);
    } catch (err) {
      toast.error("Erreur de génération IA");
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="crisis-panel p-6 max-w-lg w-full"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold heading text-primary mb-2 flex items-center gap-2">
          <Wand2 className="w-5 h-5" /> Génération IA
        </h2>
        <p className="text-sm text-muted-foreground mb-4">Décrivez votre scénario et l'IA le génère pour vous</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Type de crise</label>
            <select
              className="input-matrix w-full h-10 px-3"
              value={formData.crisis_type}
              onChange={e => setFormData({ ...formData, crisis_type: e.target.value })}
            >
              {CRISIS_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Type d'entreprise</label>
            <Input
              className="input-matrix"
              value={formData.company_type}
              onChange={e => setFormData({ ...formData, company_type: e.target.value })}
              placeholder="Ex: Site industriel SEVESO, Banque, Hôpital..."
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Description du scénario</label>
            <Textarea
              className="input-matrix"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Décrivez ce qui doit se passer : contexte, enjeux, particularités..."
              rows={4}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono uppercase text-muted-foreground">Durée (min)</label>
              <Input
                type="number"
                className="input-matrix"
                value={formData.duration_minutes}
                onChange={e => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                min={60}
                max={480}
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase text-muted-foreground">Nb événements</label>
              <Input
                type="number"
                className="input-matrix"
                value={formData.num_events}
                onChange={e => setFormData({ ...formData, num_events: parseInt(e.target.value) })}
                min={5}
                max={50}
              />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Génération...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" /> Générer
                </>
              )}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Scenario Editor (Spreadsheet-like)
export const ScenarioEditor = ({ token }) => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const loadTemplate = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scenario-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(res.data);
    } catch (err) {
      toast.error("Scénario non trouvé");
      navigate("/admin/scenarios");
    }
    setLoading(false);
  }, [templateId, token, navigate]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const saveTemplate = async (updates) => {
    setSaving(true);
    try {
      await axios.put(`${API}/scenario-templates/${templateId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Sauvegardé");
      loadTemplate();
    } catch (err) {
      toast.error("Erreur de sauvegarde");
    }
    setSaving(false);
  };

  const addEvent = async (eventData) => {
    try {
      await axios.post(`${API}/scenario-templates/${templateId}/events`, eventData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Événement ajouté");
      loadTemplate();
      setShowAddEvent(false);
    } catch (err) {
      toast.error("Erreur d'ajout");
    }
  };

  const updateEvent = async (eventId, updates) => {
    try {
      await axios.put(`${API}/scenario-templates/${templateId}/events/${eventId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Événement modifié");
      loadTemplate();
      setEditingEvent(null);
    } catch (err) {
      toast.error("Erreur de modification");
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    try {
      await axios.delete(`${API}/scenario-templates/${templateId}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Événement supprimé");
      loadTemplate();
    } catch (err) {
      toast.error("Erreur de suppression");
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Chargement...</div>;
  }

  if (!template) {
    return <div className="p-8 text-center text-destructive">Scénario non trouvé</div>;
  }

  const events = template.events || [];
  const sortedEvents = [...events].sort((a, b) => a.time_offset_minutes - b.time_offset_minutes);

  return (
    <div className="min-h-screen matrix-bg">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/admin/scenarios")}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour
            </Button>
            <div>
              <h1 className="text-xl font-bold heading text-primary">{template.name}</h1>
              <p className="text-sm text-muted-foreground">{template.crisis_type} • {template.duration_minutes} min • {events.length} événements</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowImportModal(true)}>
              <Upload className="w-4 h-4 mr-2" /> Import Excel
            </Button>
            <Button onClick={() => setShowAddEvent(true)} className="bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 mr-2" /> Ajouter événement
            </Button>
          </div>
        </div>
      </div>

      {/* Intensity Preview */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="crisis-panel p-4 mb-4">
          <div className="flex items-center gap-4 mb-2">
            <Activity className="w-5 h-5 text-primary" />
            <span className="font-mono text-sm">Courbe d'intensité</span>
          </div>
          <div className="h-16 flex items-end gap-1">
            {sortedEvents.map((event, idx) => (
              <div
                key={event.id}
                className="flex-1 bg-primary/60 hover:bg-primary transition-colors rounded-t"
                style={{ height: `${(event.intensity || 5) * 10}%` }}
                title={`${event.hour_display || ''} - Intensité: ${event.intensity || 5}`}
              />
            ))}
          </div>
        </div>

        {/* Events Table */}
        <div className="crisis-panel overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 p-3 bg-secondary/30 border-b border-border font-mono text-xs uppercase tracking-wider text-muted-foreground">
            <div className="col-span-1">Heure</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-4">Description / Contenu</div>
            <div className="col-span-1">Format</div>
            <div className="col-span-1">Intensité</div>
            <div className="col-span-1">Actions</div>
          </div>

          {/* Events */}
          <ScrollArea className="h-[calc(100vh-320px)]">
            {sortedEvents.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p>Aucun événement</p>
                <p className="text-sm">Ajoutez des événements ou importez depuis Excel</p>
              </div>
            ) : (
              sortedEvents.map((event, index) => (
                <EventRow
                  key={event.id}
                  event={event}
                  index={index}
                  isEditing={editingEvent === event.id}
                  onEdit={() => setEditingEvent(event.id)}
                  onSave={(updates) => updateEvent(event.id, updates)}
                  onCancel={() => setEditingEvent(null)}
                  onDelete={() => deleteEvent(event.id)}
                />
              ))
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Add Event Modal */}
      <AnimatePresence>
        {showAddEvent && (
          <AddEventModal onClose={() => setShowAddEvent(false)} onAdd={addEvent} />
        )}
      </AnimatePresence>

      {/* Import Excel Modal */}
      <AnimatePresence>
        {showImportModal && (
          <ImportExcelModal
            token={token}
            templateId={templateId}
            onClose={() => setShowImportModal(false)}
            onImported={() => {
              setShowImportModal(false);
              loadTemplate();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Event Row Component
const EventRow = ({ event, index, isEditing, onEdit, onSave, onCancel, onDelete }) => {
  const [editData, setEditData] = useState(event);
  const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.media;
  const contentType = CONTENT_TYPES[event.content_type] || CONTENT_TYPES.text;

  useEffect(() => {
    setEditData(event);
  }, [event]);

  if (isEditing) {
    return (
      <motion.div
        initial={{ backgroundColor: "rgba(0,255,65,0.1)" }}
        animate={{ backgroundColor: "rgba(0,255,65,0.05)" }}
        className="grid grid-cols-12 gap-2 p-3 border-b border-border"
      >
        <div className="col-span-1">
          <Input
            className="input-matrix h-8 text-sm"
            value={editData.hour_display || ""}
            onChange={e => setEditData({ ...editData, hour_display: e.target.value })}
            placeholder="08:00"
          />
        </div>
        <div className="col-span-2">
          <select
            className="input-matrix w-full h-8 px-2 text-sm"
            value={editData.event_type}
            onChange={e => setEditData({ ...editData, event_type: e.target.value })}
          >
            {Object.entries(EVENT_TYPES).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <Input
            className="input-matrix h-8 text-sm"
            value={editData.source || ""}
            onChange={e => setEditData({ ...editData, source: e.target.value })}
            placeholder="AFP, BFM..."
          />
        </div>
        <div className="col-span-4">
          <Textarea
            className="input-matrix text-sm min-h-[60px]"
            value={editData.content || ""}
            onChange={e => setEditData({ ...editData, content: e.target.value })}
            placeholder="Contenu..."
          />
        </div>
        <div className="col-span-1">
          <select
            className="input-matrix w-full h-8 px-2 text-sm"
            value={editData.content_type}
            onChange={e => setEditData({ ...editData, content_type: e.target.value })}
          >
            {Object.entries(CONTENT_TYPES).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
        </div>
        <div className="col-span-1">
          <Input
            type="number"
            className="input-matrix h-8 text-sm"
            value={editData.intensity || 5}
            onChange={e => setEditData({ ...editData, intensity: parseInt(e.target.value) })}
            min={1}
            max={10}
          />
        </div>
        <div className="col-span-1 flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => onSave(editData)} className="text-primary">
            <Check className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="grid grid-cols-12 gap-2 p-3 border-b border-border hover:bg-secondary/20 transition-colors group"
    >
      <div className="col-span-1 font-mono text-sm text-primary">
        {event.hour_display || `+${event.time_offset_minutes}m`}
      </div>
      <div className="col-span-2">
        <Badge className={`${eventType.color} text-white text-xs`}>
          {eventType.label}
        </Badge>
      </div>
      <div className="col-span-2 text-sm truncate">{event.source || "-"}</div>
      <div className="col-span-4">
        <p className="text-xs text-muted-foreground mb-1">{event.description}</p>
        <p className="text-sm line-clamp-2">{event.content}</p>
      </div>
      <div className="col-span-1">
        <Badge variant="outline" className="text-xs">
          {contentType.label}
        </Badge>
      </div>
      <div className="col-span-1">
        <div className="flex items-center gap-1">
          <div 
            className="h-2 bg-primary rounded"
            style={{ width: `${(event.intensity || 5) * 10}%` }}
          />
          <span className="text-xs text-muted-foreground">{event.intensity || 5}</span>
        </div>
      </div>
      <div className="col-span-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button size="sm" variant="ghost" onClick={onEdit}>
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} className="text-destructive">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};

// Add Event Modal
const AddEventModal = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    time_offset_minutes: 0,
    hour_display: "08:00",
    description: "",
    event_type: "media",
    content_type: "text",
    content: "",
    source: "",
    target_type: "all",
    target_roles: [],
    generate_image: false,
    intensity: 5
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Calculate time_offset from hour_display
    if (formData.hour_display) {
      const [h, m] = formData.hour_display.split(":").map(Number);
      formData.time_offset_minutes = (h - 8) * 60 + m;
    }
    onAdd(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="crisis-panel p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold heading text-primary mb-4">Ajouter un événement</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono uppercase text-muted-foreground">Heure</label>
              <Input
                className="input-matrix"
                value={formData.hour_display}
                onChange={e => setFormData({ ...formData, hour_display: e.target.value })}
                placeholder="08:15"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase text-muted-foreground">Intensité (1-10)</label>
              <Input
                type="number"
                className="input-matrix"
                value={formData.intensity}
                onChange={e => setFormData({ ...formData, intensity: parseInt(e.target.value) })}
                min={1}
                max={10}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Type d'événement</label>
            <div className="grid grid-cols-5 gap-2 mt-2">
              {Object.entries(EVENT_TYPES).map(([key, val]) => (
                <button
                  key={key}
                  type="button"
                  className={`p-2 text-xs border rounded transition-colors ${
                    formData.event_type === key 
                      ? `${val.color} text-white border-transparent` 
                      : 'border-border hover:border-primary'
                  }`}
                  onClick={() => setFormData({ ...formData, event_type: key })}
                >
                  {val.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Format du contenu</label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {Object.entries(CONTENT_TYPES).map(([key, val]) => {
                const Icon = val.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`p-3 border rounded flex items-center justify-center gap-2 transition-colors ${
                      formData.content_type === key 
                        ? 'border-primary bg-primary/20 text-primary' 
                        : 'border-border hover:border-primary'
                    }`}
                    onClick={() => setFormData({ ...formData, content_type: key, generate_image: key === 'image' })}
                  >
                    <Icon className="w-4 h-4" />
                    {val.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Source</label>
            <Input
              className="input-matrix"
              value={formData.source}
              onChange={e => setFormData({ ...formData, source: e.target.value })}
              placeholder="AFP, France Info, BFM TV, Twitter, Email interne..."
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Description (pour l'admin)</label>
            <Input
              className="input-matrix"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Ce qui se passe à ce moment..."
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">
              {formData.content_type === 'image' ? 'Prompt pour génération image' : 'Contenu'}
            </label>
            <Textarea
              className="input-matrix min-h-[120px]"
              value={formData.content}
              onChange={e => setFormData({ ...formData, content: e.target.value })}
              placeholder={formData.content_type === 'image' 
                ? "Décrivez l'image à générer : manifestants devant l'usine, fumée noire..." 
                : "Le contenu complet : dépêche AFP, article, tweet, script d'appel..."
              }
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button type="submit" className="flex-1 bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 mr-2" /> Ajouter
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Import Excel Modal
const ImportExcelModal = ({ token, templateId, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setLoading(true);

    // Parse Excel file client-side
    try {
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        setPreview(jsonData.slice(0, 10)); // Preview first 10 rows
        setLoading(false);
      };
      
      reader.readAsArrayBuffer(selectedFile);
    } catch (err) {
      toast.error("Erreur de lecture du fichier Excel");
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    try {
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        
        // Send to backend
        await axios.post(`${API}/scenario-templates/${templateId}/import-excel`, {
          scenario_id: templateId,
          data: jsonData
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success("Import réussi !");
        onImported();
      };
      
      reader.readAsArrayBuffer(file);
    } catch (err) {
      toast.error("Erreur d'import");
      setImporting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="crisis-panel p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold heading text-primary mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5" /> Import Excel
        </h2>
        
        <div className="space-y-4">
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              id="excel-upload"
            />
            <label htmlFor="excel-upload" className="cursor-pointer">
              <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {file ? file.name : "Cliquez pour sélectionner un fichier Excel"}
              </p>
            </label>
          </div>

          <div className="bg-secondary/30 p-4 rounded text-sm">
            <p className="font-semibold mb-2">Format attendu :</p>
            <p className="text-muted-foreground">
              Colonnes : <code className="text-primary">Heure | Type | Source | Description | Contenu | Image | Intensité</code>
            </p>
            <p className="text-muted-foreground mt-1">
              Types : média, social, joueur, animateur, radio
            </p>
          </div>

          {loading && (
            <div className="text-center py-4">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary" />
              <p className="text-sm text-muted-foreground mt-2">Analyse du fichier...</p>
            </div>
          )}

          {preview.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Aperçu ({preview.length} premières lignes) :</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-border">
                  <thead>
                    <tr className="bg-secondary/30">
                      {Object.keys(preview[0]).map(key => (
                        <th key={key} className="p-2 text-left border-b border-border">{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, idx) => (
                      <tr key={idx} className="border-b border-border">
                        {Object.values(row).map((val, i) => (
                          <td key={i} className="p-2 truncate max-w-[150px]">{String(val)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button 
              onClick={handleImport} 
              className="flex-1 bg-primary text-primary-foreground" 
              disabled={!file || importing}
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Import...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" /> Importer
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ScenarioTemplatesList;
